import { Injectable } from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import { guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import { ComponentFormController } from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { Observable, combineLatest, from } from 'rxjs';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { IModule } from 'module/module';
import { IModulePage } from 'module/page/module-page';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUserModule } from 'user/user-module';

import {
	SectionEditModuleState,
	SectionEditModuleStore,
} from './section-edit-module.store';

@Injectable()
@UntilDestroy()
export class SectionEditModuleController extends ComponentFormController<
	SectionEditModuleState,
	any
> {
	public module$: Observable<IModule>;
	public pages$: Observable<ReadonlyArray<IModulePage>>;
	public typeOptions$: Observable<SelectItem[]>;
	public uploading$: Observable<boolean>;

	constructor(
		store: SectionEditModuleStore,
		private _fb: FormBuilder,
		formsManager: AkitaNgFormsManager,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort
	) {
		super(
			store,
			formsManager,
			`section-edit-module-${guid()}`,
			_fb.group({ pages: _fb.array([]) })
		);

		this._initObservables();
	}

	public generateDocumentUrl(file: File, pageControl: AbstractControl): void {
		this.store.update({ uploading: true });

		from(this._moduleApplicationPort.generateModuleDocumentUrl(file))
			.pipe(
				tap((doc) =>
					pageControl.patchValue({
						urlScheme: 'https',
						activityUrl: doc.url,
						fileName: doc.fileName,
					})
				),
				finalize(() => this.store.update({ uploading: false }))
			)
			.subscribe();
	}

	public updateModule(): Promise<void> {
		const pages: [{ activityUrl: string; pageId: string; urlScheme: string }] =
			this.form.value.pages;

		return this._userApplicationPort.updateUserModule({
			userId: this.getValue().userId,
			moduleId: this.getValue().moduleId,
			pages: pages.map((page) => ({
				activityUrl: `${page.urlScheme}://${page.activityUrl}`,
				pageId: page.pageId,
			})),
		});
	}

	public updateState(moduleId: string, userId: string): void {
		this.store.update({ moduleId, userId });
	}

	private _buildForm(
		pages: ReadonlyArray<IModulePage>,
		userModule: IUserModule
	): void {
		const pagesArray = this.form.get('pages') as FormArray;
		pagesArray.clear();

		for (const page of pages) {
			const pageData = userModule.pages.find((p) => p.pageId === page.id);

			const urlData = this._extractUrlData(pageData?.activityUrl);

			const pageGroup = this._fb.group({
				activityUrl: [
					urlData[1] || '',
					[Validators.required, (control) => this.formatUrl(control)],
				],
				urlScheme: [urlData[0] || 'http', Validators.required],
				pageId: [page.id, Validators.required],
				type: ['URL', Validators.required],
				fileName: [null],
			});

			pagesArray.push(pageGroup);
		}
	}

	private _extractUrlData(url: string): string[] {
		return url
			? /(?:(http|https):\/\/){0,1}(.+)/g.exec(url).slice(1)
			: [null, null];
	}

	private _initObservables() {
		this.module$ = this.select('moduleId').pipe(
			switchMap((moduleId) =>
				this._moduleApplicationPort
					.observeModuleById(moduleId)
					.pipe(map((d) => d.data))
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		const userModules$ = this.select(['userId', 'moduleId']).pipe(
			switchMap((state) =>
				this._userApplicationPort.observeUserModules(state.userId).pipe(
					map((d) => d.data),
					map((userModules) =>
						userModules.find((um) => um.moduleId === state.moduleId)
					)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.pages$ = combineLatest([
			this.module$.pipe(
				switchMap((module) =>
					module.pages$.pipe(map((pages) => pages.filter((p) => p.hasSettings)))
				)
			),
			userModules$,
		]).pipe(
			tap(([pages, userModule]) => this._buildForm(pages, userModule)),
			map(([pages]) => pages),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.typeOptions$ = this._translateService
			.stream('presentation.app.sidebar.plan.phase.section.module.edit.types')
			.pipe(
				map(
					(i18n) =>
						[
							{ value: 'URL', label: i18n['URL'] },
							{ value: 'FILE', label: i18n['FILE'] },
						] as SelectItem[]
				),
				shareReplay({ bufferSize: 1, refCount: true })
			);

		this.uploading$ = this.select('uploading').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	private formatUrl(control: AbstractControl) {
		const urlData = this._extractUrlData(control.value);

		if (urlData[0]) {
			(control.parent as FormGroup).patchValue({
				activityUrl: urlData[1],
				urlScheme: urlData[0],
			});
		}
	}
}
